import { AppType, Wishlist, WishlistItem } from "@camilla-pihl/worker/types";
import { useToken } from "@frend-digital/centra/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { hc } from "hono/client";
import { useCallback, useMemo } from "react";
import { CentraProduct } from "../centra/atomicSetup";
import formatProductCard from "../centra/formatters/formatProductCards/formatProductCard";
import { productsQuery } from "../centra/hooks/useProducts";
import { CartItem } from "../hooks/useSelection/cart/useItems";

async function h256_128bit(apiToken: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(apiToken);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);

  // First 16 bytes (128 bits) = 22 base64url chars
  const hashArray = new Uint8Array(hashBuffer).slice(0, 16);
  return btoa(String.fromCharCode(...hashArray))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}
export type ItemId = `${string}` & {
  __brand: "ItemId";
};

const toItemId = (id: string) => id as ItemId;

type FullWishlist = Wishlist & {
  items?: Array<
    Omit<WishlistItem, "itemId"> & {
      itemId: ItemId;
    }
  >;
};

const useRpcClient = () => {
  const token = useToken();

  return useMemo(() => {
    return hc<AppType>("https://camilla-pihl-worker.frendfactory.workers.dev", {
      headers() {
        return {
          Authorization: token ? `Bearer ${token}` : "",
        };
      },
    });
  }, [token]);
};

export const useWishlistQuery = (wishlistId: string) => {
  const client = useRpcClient();
  return useQuery({
    queryKey: ["wishlist", wishlistId],
    queryFn: async () => {
      const response = await client.wishlist[":wishlistId"].$get({
        param: { wishlistId: wishlistId! },
      });

      if (!response.ok) {
        {
          throw response;
        }
      }

      const json = await response.json();

      return json as unknown as FullWishlist;
    },
    enabled: !!wishlistId,
    retry: (failureCount, error) => {
      if (error instanceof Response && error.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useWishlist = () => {
  const queryClient = useQueryClient();
  const client = useRpcClient();
  const token = useToken();

  const { data: wishlistId } = useQuery({
    queryKey: ["wishlistId", token],
    queryFn: async () => {
      if (!token) return null;

      // hash the token to make it one way
      const hash = await h256_128bit(token);
      return hash;
    },
    staleTime: Infinity,
    enabled: !!token,
  });

  const { data, error } = useWishlistQuery(wishlistId!);

  const isLoading = !data && !error;

  const has = useCallback(
    (id: ItemId | undefined | null) => {
      if (!id) return false;
      return data?.items?.some(
        (item: { itemId: ItemId }) => item.itemId === id
      );
    },
    [data]
  );

  const { mutate: add, isPending: isAdding } = useMutation({
    mutationKey: ["wishlist", wishlistId],
    onMutate: async ({ itemId, sizeItemId }) => {
      const snapshot = queryClient.getQueryData<FullWishlist>([
        "wishlist",
        wishlistId,
      ]);

      queryClient.setQueryData<FullWishlist>(
        ["wishlist", wishlistId],
        (old: FullWishlist | undefined) => {
          if (!old) return old;
          return {
            ...old,
            items: [
              ...(old?.items ?? []),
              {
                itemId: itemId,
                createdAt: new Date().toISOString() as unknown as Date,
                updatedAt: new Date().toISOString() as unknown as Date,
                wishlistId: wishlistId!,
                sizeItemId: sizeItemId || null,
              },
            ],
          };
        }
      );

      return () => {
        queryClient.setQueryData(["wishlist", wishlistId], snapshot);
      };
    },
    mutationFn: async ({
      itemId,
      sizeItemId,
    }: {
      itemId: ItemId;
      sizeItemId?: string;
    }) => {
      if (!wishlistId) throw new Error("No wishlist id");

      const response = await client.wishlist[":wishlistId"].$put({
        json: {
          itemId,
          sizeItemId: sizeItemId,
        },
        param: {
          wishlistId: wishlistId!,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to add item to wishlist", { cause: response });
      }

      const json = await response.json();

      return json.wishlist;
    },

    onSuccess: (data) => {
      queryClient.setQueryData(["wishlist", wishlistId], data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["wishlist", wishlistId] });
    },
    onError: (err, itemId, rollback) => {
      rollback?.();
    },
  });

  const { mutate: remove, isPending: isRemoving } = useMutation({
    mutationKey: ["wishlist", wishlistId],
    onMutate: async (itemId) => {
      const snapshot = queryClient.getQueryData<FullWishlist>([
        "wishlist",
        wishlistId,
      ]);

      queryClient.setQueryData<FullWishlist>(
        ["wishlist", wishlistId],
        (old: FullWishlist | undefined) => {
          if (!old) return old;
          return {
            ...old,
            items: old?.items?.filter(
              (item: WishlistItem) => item.itemId !== toItemId(itemId)
            ) ?? [],
          };
        }
      );

      return () => {
        queryClient.setQueryData(["wishlist", wishlistId], snapshot);
      };
    },
    mutationFn: async (itemId: ItemId) => {
      if (!wishlistId) throw new Error("No wishlist id");

      const response = await client.wishlist[":wishlistId"][":itemId"].$delete({
        param: {
          wishlistId: wishlistId!,
          itemId,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to remove item from wishlist", {
          cause: response,
        });
      }

      const json = await response.json();

      return json.wishlist;
    },

    onSuccess: (data) => {
      queryClient.setQueryData(["wishlist", wishlistId], data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["wishlist", wishlistId] });
    },
    onError: (err, itemId, rollback) => {
      rollback?.();
    },
  });

  const toggle = useCallback(
    (itemId: ItemId, sizeItemId?: string) => {
      if (has(itemId)) {
        remove(itemId);
      } else {
        add({ itemId, sizeItemId });
      }
    },
    [has, add, remove]
  );

  const share = useMutation({
    mutationKey: ["wishlist", wishlistId],
    mutationFn: async (name: string) => {
      if (!wishlistId) throw new Error("No wishlist id");

      const response = await client.wishlist[":wishlistId"].share.$patch({
        param: {
          wishlistId: wishlistId!,
        },
        json: {
          name: name,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to share wishlist", { cause: response });
      }

      const json = await response.json();

      return json.wishlist;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["wishlist", wishlistId], data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["wishlist", wishlistId] });
    },
  });

  return {
    data,
    isLoading,
    has,
    add,
    remove,
    toggle,
    share,
    isAdding,
    isPending: isAdding || isRemoving,
    wishlistId,
    items: data?.items || [],
  };
};

export const useProductsInWishlist = (
  wishlistId: string | null | undefined
) => {
  const { data } = useWishlistQuery(wishlistId!);

  const itemIds = useMemo(() => {
    return data?.items?.map((item: { itemId: any }) => item.itemId) || [];
  }, [data]);
  const items = data?.items || [];

  return useQuery({
    ...productsQuery({
      products: items.map((i: { itemId: any }) => i.itemId),
      onlyAvailable: false,
    }),
    enabled: !!itemIds.length,

    select: (data) => {
      // Attach size info to product card
      return data.products?.map((p) => {
        const pc = formatProductCard(p as unknown as CentraProduct);
        const item = items.find(
          (i: { itemId: string | undefined }) => i.itemId === pc.id
        );
        return {
          ...pc,
          size: pc.description?.size?.find(
            (s) => s.itemId === item?.sizeItemId
          ),
          preselectSizeId: item?.sizeItemId,
        };
      });
    },
  });
};

export type WishlistProduct = NonNullable<
  ReturnType<typeof useProductsInWishlist>["data"]
>[number];

export const cartItemToItemId = (item: CartItem) => {
  return `${item.id}-${item.variant}-${item.sku}` as ItemId;
};

export const getItemIdParts = (itemId: ItemId): WishlistItemIdParts => {
  const [displayItemId, variant, sku] = itemId.split("-");
  return { displayItemId };
};

export type WishlistItemIdParts = {
  displayItemId: string;
};

export const createItemId = ({ displayItemId }: WishlistItemIdParts) => {
  return `${displayItemId}` as ItemId;
};

export const wishlist = {
  getItemIdParts,
  createItemId,
  cartItemToItemId,
  useWishlist,
  useWishlistQuery,
  useProductsInWishlist,
  toItemId,
};
